import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import * as bootstrap from 'bootstrap';

import Logo from '../../../assets/images/grhamDeal-logo.png';
import './Navbar.scss';

import SignInPopupHOC from '../../../pages/Login/hoc/SigninHoc';
import SignIn from '../../../pages/Login/Signin';
import SignUpPopupHOC from '../../../pages/SignUp/hoc/SignupHoc';
import SignUp from '../../../pages/SignUp/Signup';
import DashboardNavbar from './DashboardNavbar';

import {
  loggedInUserState,
  resetUserData,
} from '../../../redux/slices/LoginSlice';
import axiosCall from '../../../utils/axiosCall';
import ConfirmationPopup from '../../../utils/ConfirmationPopup/ConfirmationPopup';
import Loader from '../Loader/Loader';
import { useSocket } from '../../../context/SocketProvider';

interface IProps {
  showSignInPopup: boolean;
  signInToggleHandler: () => void;
  showSignUpPopup: boolean;
  signUpToggleHandler: () => void;
  isDashboard: boolean;
}


function TopNav(props: IProps) {
  const {
    signInToggleHandler,
    showSignInPopup,
    signUpToggleHandler,
    showSignUpPopup,
    isDashboard,
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socket = useSocket();
  const userData = useSelector(loggedInUserState);
  const isLoggedIn = Boolean(userData);

  const [doLogout, setDoLogout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);

  // Offer Banner State
  const [showOfferBanner, setShowOfferBanner] = useState(true);


  const collapseRef = useRef<HTMLDivElement | null>(null);
  const bsCollapseRef = useRef<bootstrap.Collapse | null>(null);

  useEffect(() => {
    if (collapseRef.current) {
      bsCollapseRef.current = new bootstrap.Collapse(collapseRef.current, {
        toggle: false,
      });
    }

    return () => {
      if (bsCollapseRef.current) {
        bsCollapseRef.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const localUser = localStorage.getItem('user');

    if (localUser && token) {
      try {
        const decodedToken: any = jwtDecode(token);
        const currentDate = new Date();

        if (decodedToken.exp * 1000 < currentDate.getTime()) {
          handleLogout();
        }
      } catch (err) {
        console.error('Invalid token:', err);
        handleLogout();
      }
    }
  }, [navigate]);

  const closeSidebar = () => {
    if (collapseRef.current && collapseRef.current.classList.contains('show')) {
      bsCollapseRef.current?.hide();
      setIsNavbarOpen(false);
    }
  };

  const toggleNavbar = () => {
    if (bsCollapseRef.current) {
      bsCollapseRef.current.toggle();
      setIsNavbarOpen((prev) => !prev);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      const userID = userData?._id;

      navigate('/');
      dispatch(resetUserData(null));
      localStorage.clear();

      if (socket) {
        socket.emit('logout-me', userID);
      }

      setDoLogout(false);
      await axiosCall.post(`/auth/logout/${userID}`);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (isDashboard) {
    return <DashboardNavbar />;
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <React.Fragment>

      {showSignInPopup && (
        <SignIn
          modalClosedSignIn={signInToggleHandler}
          modalClosedSignUp={signUpToggleHandler}
        />
      )}

      {showSignUpPopup && (
        <SignUp
          modalClosedSignUp={signUpToggleHandler}
          modalClosedSignIn={signInToggleHandler}
        />
      )}

      {doLogout && (
        <ConfirmationPopup
          onModalClose={() => setDoLogout(false)}
          onSubmit={handleLogout}
          confirmationText="Are you sure you want to log out?"
          saveButtonText="Yes"
          cancelButtonText="No"
          modalTitle="Logout"
          isProgress={loading}
        />
      )}

      <header className="fixed-top">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-auto col-xl pe-0">
              <nav className="navbar navbar-expand-xl">
                <div className="container-fluid p-0">
                  <button
                    className={`navbar-toggler ps-0 ${isNavbarOpen ? 'is-active' : ''}`}
                    type="button"
                    onClick={toggleNavbar}
                    aria-controls="navbarSupportedContent"
                    aria-expanded={isNavbarOpen}
                    aria-label="Toggle navigation"
                  >
                    <div className="hamburger-icon">
                      <span className="line"></span>
                      <span className="line"></span>
                      <span className="line"></span>
                    </div>
                  </button>

                  <Link
                    to="/"
                    className="navbar-brand custom-logo"
                    onClick={closeSidebar}
                  >
                    <img
                      src={Logo}
                      alt="GrhamDeal Logo"
                      className="logo img-fluid"
                    />
                  </Link>

                  <div
                    ref={collapseRef}
                    className="collapse navbar-collapse"
                    id="navbarSupportedContent"
                    style={{ top: showOfferBanner ? '112px' : '49px' }}
                  >
                    <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
                      <li className="nav-item" onClick={closeSidebar}>
                        <NavLink to="/" className="nav-link">
                          Home
                        </NavLink>
                      </li>
                      <li className="nav-item" onClick={closeSidebar}>
                        <NavLink to="/housing-inquiry" className="nav-link">
                          Housing Inquiry
                        </NavLink>
                      </li>
                      <li className="nav-item" onClick={closeSidebar}>
                        <NavLink to="/blog" className="nav-link">
                          Blog
                        </NavLink>
                      </li>
                      <li className="nav-item" onClick={closeSidebar}>
                        <NavLink to="/aboutus" className="nav-link">
                          About Us
                        </NavLink>
                      </li>
                      <li className="nav-item" onClick={closeSidebar}>
                        <NavLink to="/pricing" className="nav-link">
                          Pricing
                        </NavLink>
                      </li>
                      <li className="nav-item" onClick={closeSidebar}>
                        <NavLink to="/faq" className="nav-link">
                          FAQ
                        </NavLink>
                      </li>
                    </ul>
                  </div>
                </div>
              </nav>
            </div>

            <div className="col col-xl-auto ps-0">
              <div className="header-right d-flex align-items-center justify-content-end">
                {isLoggedIn ? (
                  <>
                    <NavLink
                      className="btn btn-link d-flex align-items-center me-2"
                      to="/dashboard"
                      onClick={closeSidebar}
                    >
                      Dashboard
                    </NavLink>
                    <button
                      className="btn btn-primary focus-btn"
                      onClick={() => setDoLogout(true)}
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-primary sign-up me-2 focus-btn"
                      onClick={signUpToggleHandler}
                    >
                      <svg
                        className="me-2"
                        width="18"
                        height="18"
                        viewBox="0 0 18 20"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M14.0273 14.7852C13.2188 13.4141 11.7773 12.5 10.125 12.5H7.875C6.1875 12.5 4.74609 13.4141 3.97266 14.7852C5.20312 16.1562 6.99609 17 9 17C10.9688 17 12.7617 16.1562 14.0273 14.7852ZM0 10.25C0 7.05078 1.6875 4.09766 4.5 2.48047C7.27734 0.863281 10.6875 0.863281 13.5 2.48047C16.2773 4.09766 18 7.05078 18 10.25C18 13.4844 16.2773 16.4375 13.5 18.0547C10.6875 19.6719 7.27734 19.6719 4.5 18.0547C1.6875 16.4375 0 13.4844 0 10.25ZM9 10.8125C9.87891 10.8125 10.7227 10.3555 11.1797 9.54688C11.6367 8.77344 11.6367 7.82422 11.1797 7.01562C10.7227 6.24219 9.87891 5.75 9 5.75C8.08594 5.75 7.24219 6.24219 6.78516 7.01562C6.32812 7.82422 6.32812 8.77344 6.78516 9.54688C7.24219 10.3555 8.08594 10.8125 9 10.8125Z" />
                      </svg>
                      Sign up
                    </button>
                    <button
                      className="btn btn-primary focus-btn"
                      onClick={signInToggleHandler}
                    >
                      Member Login
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
    </React.Fragment>
  );
}

export default SignUpPopupHOC(SignInPopupHOC(TopNav));
